import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { catchError, of, switchMap, tap } from 'rxjs';

import { DialogCloseReason } from '@sui/dialog';
import { SuiDialogBase } from '@sui/dialog/sui-dialog-base';

import { AppState } from 'src/app/reducers';
import {
  ContentFilters,
  Dimension,
  Feed,
  Post,
  User,
} from 'src/app/core/models/core.models';
import { FeedPostViewMode } from 'src/app/core/services/feed.service';
import { FeedEntityService } from 'src/app/core/services/feed-entity.service';
import { FeedApiService } from 'src/app/api/queries/feed-api.service';
import { selectFeedTimeShown, selectUser } from 'src/app/core/reducers';
import { TranslateService } from '@ngx-translate/core';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';
import { DimensionJoinTypeId, NomObjectTypes } from 'src/app/core/constants';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import { formatDate } from 'date-fns';

import { ButtonConfig } from 'src/app/admin/models';

interface DialogData {
  feedId: string;
  contentFilters?: Partial<ContentFilters>;
  pivotFeedId?: string;
  filterDimensions?: Dimension[];
  filterLabel?: string;
}

interface DialogResult {
  navigate: boolean;
  workspaceId: string;
}

interface PreviewBreadcrumbItem {
  label: string;
  className: string;
  icon?: IconProp;
}

/** A row in the projected view-dropdown panel (was a `DropdownItem` config). */
interface ViewMenuItem {
  text?: string;
  icon?: IconProp;
  key?: string;
  value?: unknown;
  isTitle?: boolean;
  isSeparator?: boolean;
  isRadio?: boolean;
  isToggle?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
  action?: () => void;
}

/** A run of consecutive same-`radioGroup` rows, or a single non-radio row. */
interface ViewMenuGroup {
  radioGroup?: string;
  items: ViewMenuItem[];
}

@Component({
  selector: 'app-feed-results-preview-dialog',
  templateUrl: './feed-results-preview-dialog.component.html',
  styleUrl: './feed-results-preview-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FeedResultsPreviewDialogComponent
  extends SuiDialogBase<DialogData, DialogResult>
  implements OnInit
{
  private destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private feedEntity = inject(FeedEntityService);
  private feedApi = inject(FeedApiService);
  private translateService = inject(TranslateService);
  private feedSettingsService = inject(FeedSettingsService);
  private cdr = inject(ChangeDetectorRef);

  dialogTitle = this.translateService.instant('feed-results-preview.title');

  protected buttons: ButtonConfig[] = [
    {
      variant: 'secondary',
      text: this.translateService.instant(
        'feed-preview-popup.button.view-in-feed',
      ),
      leadingIcon: ['fas', 'arrow-up-right-from-square'],
      action: () => this.navigateToFeed(),
    },
    {
      variant: 'primary',
      text: this.translateService.instant('feed-preview-popup.button.close'),
      leadingIcon: ['fas', 'xmark'],
      action: () => this.close({ reason: DialogCloseReason.Cancel }),
    },
  ];

  posts: Post[] = [];
  postsLoading = false;
  user: User;
  timeShown = 'post_time_published_relative';

  currentPage = 1;
  itemsPerPage = 30;
  totalItemsCount = 0;
  itemsPerPageOptions: number[] = [10, 20, 30, 50, 100];

  viewMode: FeedPostViewMode = 'default';
  viewConfig: PropertyViewConfig[] = [];
  /** Rows of the projected View dropdown; `viewValues` holds each radio group's selection. */
  viewMenuItems: ViewMenuItem[] = [];
  viewMenuGroups: ViewMenuGroup[] = [];
  viewValues: Record<string, unknown> = {};
  breadcrumbItems: PreviewBreadcrumbItem[] = [];

  private feed: Feed | null = null;
  private pivotDimension: Dimension | null = null;

  get feedId(): string {
    return this.dialogData.feedId;
  }

  ngOnInit(): void {
    this.store
      .select(selectUser)
      .pipe(
        tap((user) => (this.user = user)),
        switchMap(() =>
          // `resolveWorkspace` is asked for here only - the breadcrumb names the
          // feed's workspace.
          this.feedEntity.getFeed(this.feedId, true).pipe(
            tap((feed) => {
              this.feed = feed;
              this.initViewDropdown(feed.object_type_id);
              this.dialogTitle = this.translateService.instant(
                'feed-results-preview.title',
                {
                  feed_name: feed.title,
                  period:
                    this.dialogData.contentFilters?.time_start &&
                    this.dialogData.contentFilters?.time_end
                      ? `(${new Date(this.dialogData.contentFilters.time_start * 1000).toLocaleDateString()} - ${new Date(this.dialogData.contentFilters.time_end * 1000).toLocaleDateString()})`
                      : '',
                },
              );
              this.buildBreadcrumbItems(feed);
            }),
            catchError(() => {
              this.initViewDropdown(NomObjectTypes.COMBINED_FEED);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectFeedTimeShown)
      .pipe(
        tap((timeShown) => (this.timeShown = timeShown)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    if (this.dialogData.pivotFeedId) {
      this.loadPivotFeedAndPosts();
    } else {
      this.loadPosts();
    }
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.loadPosts();
  }

  onItemsPerPageChange(value: number): void {
    this.itemsPerPage = value;
    this.currentPage = 1;
    this.loadPosts();
  }

  closeDialog(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }

  navigateToFeed(): void {
    const workspaceId = this.feed?.workspace_id;
    if (!workspaceId) return;

    this.close({
      reason: DialogCloseReason.Confirm,
      data: { navigate: true, workspaceId },
    });
  }

  private buildBreadcrumbItems(feed: Feed): void {
    const items: PreviewBreadcrumbItem[] = [];

    const workspaceTitle = feed.workspace?.title;
    if (workspaceTitle) {
      items.push({
        label: workspaceTitle,
        className: 'workspace',
        icon: ['fas', 'layer-group'],
      });
    }

    items.push({
      label: feed.title,
      className: 'feed',
      icon: getIconForDataObject(feed.object_class_id, feed.object_type_id),
    });

    if (this.dialogData.filterDimensions?.length) {
      for (const dim of this.dialogData.filterDimensions) {
        items.push({
          label: dim.title,
          className:
            dim.join_type_id === DimensionJoinTypeId.AND_NOT
              ? 'dimension not-active'
              : 'dimension',
          icon: ['fas', 'link'],
        });
      }
    }

    if (this.dialogData.filterLabel) {
      items.push({
        label: this.dialogData.filterLabel,
        className: 'filter',
        icon: ['fas', 'filter'],
      });
    }

    const timeStart = this.dialogData.contentFilters?.time_start;
    const timeEnd = this.dialogData.contentFilters?.time_end;
    if (timeStart && timeEnd) {
      const startStr = formatDate(
        new Date(timeStart * 1000),
        'dd MMM yyyy, HH:mm',
      );
      const endStr = formatDate(new Date(timeEnd * 1000), 'dd MMM yyyy, HH:mm');
      items.push({
        label: `${startStr} - ${endStr}`,
        className: 'period',
        icon: ['far', 'calendar'],
      });
    }

    this.breadcrumbItems = items;
  }

  private async initViewDropdown(objectTypeId: NomObjectTypes): Promise<void> {
    const config = await this.feedSettingsService.getFeedConfig(
      objectTypeId,
      this.user,
    );
    this.viewConfig = config;

    this.buildViewDropdownItems();
  }

  private buildViewDropdownItems(): void {
    const createMenuItem = (configItem: PropertyViewConfig): ViewMenuItem[] => {
      if (configItem.isTitle) {
        return [
          { isSeparator: true },
          { text: configItem.label, isTitle: true },
        ];
      }

      return [
        {
          text: configItem.label,
          isToggle: !configItem.isRadio,
          key: configItem.path as string,
          isSelected: configItem.selected,
          isRadio: configItem.isRadio,
          radioGroup: configItem.radioGroup,
          isSeparator: false,
          value: configItem.path,
          action: () => {
            if (configItem.isRadio) {
              if (configItem.radioGroup === 'view_posts_mode') {
                this.viewMode = configItem.path as FeedPostViewMode;
                this.viewValues = {
                  ...this.viewValues,
                  view_posts_mode: configItem.path,
                };
              } else if (configItem.radioGroup === 'post_time_mode') {
                this.timeShown = configItem.path as string;
                this.viewValues = {
                  ...this.viewValues,
                  post_time_mode: configItem.path,
                };
                this.viewConfig.forEach((opt) => {
                  if (opt.radioGroup === 'post_time_mode') {
                    opt.selected = opt.label === configItem.label;
                  }
                });
              }
            } else {
              // Map the array to create a new one with the toggled item
              this.viewConfig = this.viewConfig.map((item) => {
                if (item.path === configItem.path) {
                  return { ...item, selected: !item.selected };
                }
                return item;
              });
            }
            this.buildViewDropdownItems();
          },
          icon: configItem.icon,
        },
      ];
    };

    const tools = this.viewConfig.filter((c) =>
      c.visibility?.includes('tools'),
    );
    const properties = this.viewConfig.filter(
      (c) => !c.visibility?.includes('tools'),
    );

    const metadataHeader: ViewMenuItem = {
      isTitle: true,
      text: this.translateService.instant('feed-actions.heading.metadata'),
    };

    const convertedArray: ViewMenuItem[] = [
      ...tools.flatMap(createMenuItem),
      metadataHeader,
      ...properties.flatMap(createMenuItem),
    ];

    this.viewMenuItems = convertedArray.filter(
      (item, index) => !(index === 0 && item.isSeparator),
    );
    this.viewMenuGroups = this.groupViewItems(this.viewMenuItems);

    this.viewValues = {
      ...this.viewValues,
      view_posts_mode: this.viewMode,
      post_time_mode: this.timeShown,
    };

    // Force a new reference for the config array (post-card [viewConfig] binding)
    this.viewConfig = [...this.viewConfig];

    // Manually trigger detection (rebuild happens outside Angular's event flow)
    this.cdr.detectChanges();
  }

  /** Group consecutive same-`radioGroup` rows so each run gets one radiogroup wrapper. */
  private groupViewItems(items: ViewMenuItem[]): ViewMenuGroup[] {
    const groups: ViewMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        continue;
      }

      groups.push({ radioGroup: item.radioGroup, items: [item] });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: ViewMenuGroup, value: unknown): void {
    group.items.find((item) => item.value === value)?.action?.();
  }

  private getDefaultContentFilters(): ContentFilters {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const currentTimeUnix = Math.floor(now.getTime() / 1000);
    const thirtyDaysAgo = currentTimeUnix - 2592000;

    return {
      time_start: thirtyDaysAgo,
      time_end: currentTimeUnix,
      offset: 0,
      items_count: this.itemsPerPage,
      sort: 'desc',
      selected_only: false,
      order: 'post_time_published',
    };
  }

  private loadPivotFeedAndPosts(): void {
    this.postsLoading = true;

    this.feedEntity
      .getFeed(this.dialogData.pivotFeedId)
      .pipe(
        tap((feed) => (this.pivotDimension = feed as Dimension)),
        catchError(() => {
          this.pivotDimension = null;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadPosts());
  }

  private getDimensionFilters(): Record<
    string,
    { include: string[]; exclude: string[] }
  > {
    const queries: string[] = [];

    if (this.pivotDimension) {
      const query = this.pivotDimension.fq ?? this.pivotDimension.query;
      if (query) queries.push(query);
    }

    if (this.dialogData.filterDimensions?.length) {
      for (const dim of this.dialogData.filterDimensions) {
        const query = dim.fq ?? dim.query;
        if (query) queries.push(query);
      }
    }

    if (queries.length === 0) return {};

    return {
      post_content_search: { include: queries, exclude: [] },
    };
  }

  private loadPosts(): void {
    this.postsLoading = true;

    const dimensionFilters = this.getDimensionFilters();
    const existingFilters = this.dialogData.contentFilters?.filters ?? {};

    const contentFilters: ContentFilters = {
      ...this.getDefaultContentFilters(),
      ...this.dialogData.contentFilters,
      offset: (this.currentPage - 1) * this.itemsPerPage,
      items_count: this.itemsPerPage,
      filters: {
        ...existingFilters,
        ...dimensionFilters,
      },
    };

    this.feedApi
      .getFeedPosts(this.feedId, contentFilters)
      .pipe(
        tap((response) => {
          this.posts = response.posts || [];
          this.totalItemsCount = response.count_posts || 0;
          this.postsLoading = false;
        }),
        catchError(() => {
          this.postsLoading = false;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
